import appConfigs from "@configs/app";
import { isDefined, isEmpty } from "@utils/var.check";
import { userGoogleCredential } from "@plugins/google/user.google.credential";
import { googleAuth } from "@plugins/google/google.auth";
import { PICKER_ERROR } from "@plugins/google/google.constant";

class GoogleDrive {
  scriptLoadStatus = "NOTLOADED";
  constructor() {
    // empty
  }

  async getUserDriveFileData(documentId) {
    if (this.scriptLoadStatus === "NOTLOADED") {
      this.scriptLoadStatus = "LOADING";
      await this.loadScript().then(() => {
        this.scriptLoadStatus = "LOADED";
      });
    } else if (this.scriptLoadStatus === "LOADING") {
      await this.getScriptLoadStatusUntilLoaded(0);
    } else if (this.scriptLoadStatus === "LOADED") {
      await this.validateAndRefreshCredential();
    }

    return new Promise((resolve, reject) => {
      if (googleAuth.requireSignIn) {
        this.relinkAccountAndRetrieveFileData(documentId, resolve, reject);
      } else {
        this.retrieveFileData(documentId, resolve, reject);
      }
    });
  }

  async loadScript() {
    await this.init();
  }

  async init() {
    try {
      if (window.client) {
        return;
      }

      if (!isDefined(window.gapi)) {
        const scriptConfig = appConfigs.googleApiLibraryScript;
        const scriptEl = document.createElement("script");

        for (const key in scriptConfig) {
          if (scriptConfig[key]) {
            scriptEl.setAttribute(key, scriptConfig[key]);
          }
        }

        await new Promise((resolve, reject) => {
          scriptEl.onload = resolve;
          scriptEl.onerror = reject;
          document.head.appendChild(scriptEl);
        });
      }

      await this.loadClient();
    } catch (err) {
      console.error("Failed to initialize Google API:", err);
      throw err;
    }
  }

  async loadClient() {
    try {
      // load gapi client
      await new Promise((resolve) => {
        window.gapi.load("client", resolve);
      });

      //init gapi client
      await googleAuth.fetchGoogleClientCredential();
      window.client = await window.gapi.client.init({
        apiKey: googleAuth.googleClientCredential!.developerKey,
      });

      await this.validateAndRefreshCredential();

      // load drive api
      await new Promise((resolve) => {
        window.gapi.client.load("drive", "v3", resolve);
      });
    } catch (err) {
      console.error("Failed to load Google client:", err);
      throw err;
    }
  }

  async validateAndRefreshCredential() {
    await userGoogleCredential.fetchLinkedAccountCredential();
    const credential = userGoogleCredential.getCredential();

    if (isEmpty(credential.userLinkedEmail)) {
      throw PICKER_ERROR.EMPTY_EMAIL;
    }

    if (credential.isAccessTokenExpired) {
      await userGoogleCredential.refreshLinkedAccountCredential();
      this.setToken(userGoogleCredential.getAccessToken());
    } else {
      this.setToken(credential.accessToken);
    }
  }

  setToken(accessToken) {
    window.gapi.client.setToken({
      access_token: accessToken,
    });
  }

  retrieveFileData(documentId, resolve, reject) {
    window.gapi.client.drive.files
      .get({
        fileId: documentId,
        fields:
          "id,name,createdTime,mimeType,webViewLink,modifiedTime,owners,lastModifyingUser,description,size,webContentLink,exportLinks,fileExtension,iconLink,capabilities",
      })
      .then(
        (response) => {
          if (response.status !== 200) {
            reject(response.status);
          }
          resolve(response.result);
        },
        (error) => {
          reject(error);
        },
      );
  }

  relinkAccountAndRetrieveFileData(documentId, resolve, reject) {
    googleAuth.checkUserCodeExpire().then(
      () => {
        this.retrieveFileData(documentId, resolve, reject);
      },
      (error) => {
        reject({
          message: error,
        });
      },
    );
  }

  async getScriptLoadStatusUntilLoaded(retryNo) {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        if (this.scriptLoadStatus === "LOADED") {
          resolve();
        } else if (retryNo <= 10) {
          this.getScriptLoadStatusUntilLoaded(retryNo + 1);
        } else {
          reject();
        }
      }, 1500);
    });
  }
}

const googleDrive = new GoogleDrive();

export { googleDrive };
